'use client'

import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { GradeScale } from './GradeScale'
import { StatStrip, type Stat } from './StatStrip'
import { TrendSparkline } from './TrendSparkline'
import type { TrendPoint } from './useTrendHistory'
import { InstrumentLabel, Panel } from './ui'

/**
 * The dashboard headline: grade, score, movement, position on the A-F scale,
 * plain-language verdict, trend, and the four figures that frame it.
 *
 * The verdict copy is NOT authored here — it comes from `interpretation`,
 * which the caller builds with `buildVerdict()` (src/lib/radar/verdict.ts),
 * the same producer ScoreInterpretation reads its impact line from. One
 * source for the sentence, two places it can appear.
 *
 * Withheld runs are a first-class state, not an error: when `score` is null
 * the grade letter, the /100 figure and the scale marker are all suppressed
 * and replaced with an explicit "not scored" reading. A score nobody earned
 * is never rendered.
 */

export interface ScoreHeroProps {
  /** null when every completed tool withheld its grade. */
  score: number | null
  grade: string
  /** Tools that produced a usable score — the denominator behind `score`. */
  scoredCount: number
  /** Completed tools that declined to grade. */
  withheldCount: number
  interpretation: { headline: string; description: string } | null
  previousScore?: number | null
  activeDomain?: string | null
  stats: Stat[]
  /** Hidden while a scan is mid-flight; the series is not final yet. */
  showTrend?: boolean
  /**
   * Pre-supplied trend series. When omitted the sparkline fetches the real
   * history for `activeDomain`, which is what the dashboard wants. Marketing
   * surfaces (see components/home/RadarDemo) MUST pass this: the trend route
   * is authenticated, so an anonymous visitor would otherwise trigger a 401
   * and render the error state on a public page.
   */
  trend?: TrendPoint[]
  /** Optional exact provenance caption for non-dashboard consumers. */
  scoreCaption?: string
}

export function ScoreHero({
  score,
  grade,
  scoredCount,
  withheldCount,
  interpretation,
  previousScore,
  activeDomain,
  stats,
  showTrend = true,
  trend,
  scoreCaption,
}: ScoreHeroProps) {
  const hasScore = score !== null
  const gradeColor = hasScore
    ? (GRADE_COLORS[grade] ?? 'var(--muted-foreground)')
    : 'var(--muted-foreground)'

  const delta =
    hasScore && previousScore !== null && previousScore !== undefined
      ? score - previousScore
      : null

  return (
    <Panel gradientRule>
      <div className='grid grid-cols-1 lg:grid-cols-[minmax(380px,440px)_1fr]'>
        {/* ── Score ─────────────────────────────────────────────────── */}
        <div className='flex flex-col gap-5 border-b border-hairline px-6 py-7 sm:px-8 lg:border-b-0 lg:border-r'>
          <InstrumentLabel tone='secondary'>AI readiness score</InstrumentLabel>

          {hasScore ? (
            <div className='flex flex-wrap items-baseline gap-x-5 gap-y-2'>
              <span
                className='font-display text-[72px] font-bold leading-none tracking-[-0.03em] sm:text-[96px]'
                style={{ color: gradeColor }}
              >
                {grade}
              </span>
              <span className='font-display text-[31px] font-medium tracking-[-0.02em]'>
                {score}
                <span className='text-instrument text-[25px]'>/100</span>
              </span>
              {delta !== null && <DeltaBadge delta={delta} />}
            </div>
          ) : (
            <div className='flex flex-col gap-2'>
              <span className='font-display text-[44px] font-medium leading-none tracking-[-0.02em] text-muted-foreground'>
                Not scored
              </span>
              <span className='block text-sm text-muted-foreground'>
                {withheldCount > 0
                  ? `Every completed check declined to grade${
                      activeDomain ? ` ${activeDomain}` : ''
                    }. A withheld grade is a refusal to score, not a zero.`
                  : 'No gradeable results yet.'}
              </span>
            </div>
          )}

          <GradeScale score={score} />

          {interpretation && (
            <div className='flex flex-col gap-1.5'>
              <span className='block text-base font-semibold'>
                {interpretation.headline}
              </span>
              <span className='block text-pretty text-sm leading-relaxed text-muted-foreground'>
                {interpretation.description}
              </span>
            </div>
          )}

          <span className='block text-xs text-instrument'>
            {scoreCaption ??
              (hasScore
                ? `Mean across ${scoredCount} gradeable ${scoredCount === 1 ? 'tool' : 'tools'}`
                : 'No gradeable tools in this run')}
            {withheldCount > 0 &&
              ` · ${withheldCount} withheld ${withheldCount === 1 ? 'grade' : 'grades'}`}
          </span>
        </div>

        {/* ── Trend + figures ───────────────────────────────────────── */}
        <div className='flex flex-col'>
          {showTrend ? (
            <TrendSparkline domain={activeDomain} data={trend} />
          ) : (
            <div className='flex flex-1 items-center px-8 py-7'>
              <span className='text-xs text-instrument'>
                Trend updates when the audit finishes.
              </span>
            </div>
          )}
          <StatStrip stats={stats} />
        </div>
      </div>
    </Panel>
  )
}

/**
 * Movement against the previous saved run. Carries an icon as well as a
 * colour so the direction survives without colour perception.
 *
 * Exported for `DashboardHero`, which forks the hero LAYOUT but must not fork
 * this badge — the wording and the colour/icon pairing are the same claim
 * about the same delta on both surfaces.
 */
export function DeltaBadge({ delta }: { delta: number }) {
  const color =
    delta > 0
      ? 'var(--growth)'
      : delta < 0
        ? 'var(--cta)'
        : 'var(--pm-text-muted)'
  const Icon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus
  return (
    <span
      className='inline-flex items-center gap-1 font-mono text-xs tabular-nums'
      style={{ color }}
      aria-label={
        delta > 0
          ? `${delta} points higher than the last scan`
          : delta < 0
            ? `${Math.abs(delta)} points lower than the last scan`
            : 'unchanged since the last scan'
      }
    >
      <Icon size={12} aria-hidden='true' />
      <span aria-hidden='true'>
        {delta > 0 ? `+${delta}` : delta === 0 ? '±0' : delta} vs last scan
      </span>
    </span>
  )
}
